/**
 * BulkMailRegistrationPage — `/mail/register/bulk`.
 *
 * Live operator surface backed by service-mail
 *   POST /v1/bulk-submissions
 *   GET  /v1/bulk-submissions
 *
 * UI flow:
 *   1. Operator picks a CSV file (or pastes CSV content).
 *   2. We parse client-side (simple comma split — V1 contract).
 *   3. Preview the parsed rows.
 *   4. Submit → backend processes synchronously and returns per-row results.
 *   5. Render the OK / ERROR breakdown + per-row table.
 */
import { type ChangeEvent, type JSX, useEffect, useState } from 'react';
import { CheckCircle2, FileSpreadsheet, Loader2, Upload, XCircle } from 'lucide-react';

import {
  listBulkSubmissions,
  submitBulkMail,
  type BulkSubmissionDetailDto,
  type BulkSubmissionDto,
} from '../../lib/api/bulkMail';
import { PageHeader } from '../shared/StaffUI';

const HEADERS = [
  'senderName',
  'senderPhone',
  'senderAddress',
  'receiverName',
  'receiverPhone',
  'receiverAddress',
  'weightGrams',
  'declaredValueMinor',
  'serviceClass',
] as const;

interface ParsedRow {
  raw: Record<string, string>;
  parsed: Record<string, unknown>;
}

function splitCsvLine(line: string): string[] {
  // Minimal CSV split — V1 doesn't support quoted commas; the spec says
  // "simple split-by-comma" so we keep the parser tiny and predictable.
  return line.split(',').map((c) => c.trim());
}

function parseCsv(content: string): { headers: string[]; rows: ParsedRow[] } {
  const lines = content
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  const firstLine = lines[0];
  if (firstLine === undefined) return { headers: [], rows: [] };
  const headers = splitCsvLine(firstLine);
  const rows: ParsedRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const lineI = lines[i];
    if (lineI === undefined) continue;
    const cells = splitCsvLine(lineI);
    const raw: Record<string, string> = {};
    const parsed: Record<string, unknown> = {};
    headers.forEach((h, idx) => {
      const v = cells[idx] ?? '';
      raw[h] = v;
      if (h === 'weightGrams' || h === 'declaredValueMinor') {
        const n = parseInt(v, 10);
        parsed[h] = Number.isNaN(n) ? v : n;
      } else {
        parsed[h] = v;
      }
    });
    rows.push({ raw, parsed });
  }
  return { headers, rows };
}

export function BulkMailRegistrationPage(): JSX.Element {
  const [filename, setFilename] = useState('');
  const [csvContent, setCsvContent] = useState('');
  const [parsed, setParsed] = useState<{ headers: string[]; rows: ParsedRow[] }>({ headers: [], rows: [] });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkSubmissionDetailDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<readonly BulkSubmissionDto[]>([]);

  async function refreshHistory(): Promise<void> {
    try {
      const res = await listBulkSubmissions({ pageSize: 10 });
      setHistory(res.items);
    } catch {
      // Non-fatal
    }
  }

  useEffect(() => {
    void refreshHistory();
  }, []);

  function onFile(e: ChangeEvent<HTMLInputElement>): void {
    const f = e.target.files?.[0];
    if (!f) return;
    setFilename(f.name);
    const reader = new FileReader();
    reader.onload = (): void => {
      const text = String(reader.result ?? '');
      setCsvContent(text);
      setParsed(parseCsv(text));
      setResult(null);
      setError(null);
    };
    reader.readAsText(f);
  }

  function onPasteParse(): void {
    setParsed(parseCsv(csvContent));
    setResult(null);
    setError(null);
  }

  async function onSubmit(): Promise<void> {
    if (parsed.rows.length === 0) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const res = await submitBulkMail({
        filename: filename || 'pasted.csv',
        rows: parsed.rows.map((r) => r.parsed),
      });
      setResult(res);
      await refreshHistory();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-5" data-testid="bulk-mail-page">
      <PageHeader
        title="Bulk Mail Submission"
        description="Upload a CSV (or paste rows) and submit a batch. Each row is processed synchronously."
      />

      <div className="bg-white border border-border rounded-xl p-5 space-y-3">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4 text-nipost-green" /> Upload / paste CSV
        </h3>
        <p className="text-xs text-muted">
          Expected headers: <code className="font-mono">{HEADERS.join(', ')}</code>
        </p>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={onFile}
          data-testid="bulk-file-input"
          className="block text-sm"
        />
        <textarea
          value={csvContent}
          onChange={(e) => setCsvContent(e.target.value)}
          placeholder="Or paste CSV content here…"
          data-testid="bulk-csv-textarea"
          rows={6}
          className="w-full px-3 py-2 text-xs font-mono border border-border rounded-lg"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onPasteParse}
            disabled={csvContent.trim().length === 0}
            data-testid="bulk-parse"
            className="px-3 py-1.5 text-sm font-semibold border border-border rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Parse
          </button>
          <button
            type="button"
            onClick={() => void onSubmit()}
            disabled={submitting || parsed.rows.length === 0}
            data-testid="bulk-submit"
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Submit {parsed.rows.length} row{parsed.rows.length === 1 ? '' : 's'}
          </button>
        </div>
        {error ? <p className="text-sm text-red-700">{error}</p> : null}
      </div>

      {parsed.rows.length > 0 && !result ? (
        <div className="bg-white border border-border rounded-xl p-4 overflow-x-auto" data-testid="bulk-preview">
          <h3 className="font-semibold text-gray-900 mb-2">Preview ({parsed.rows.length} row{parsed.rows.length === 1 ? '' : 's'})</h3>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-muted">
              <tr>{parsed.headers.map((h) => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr>
            </thead>
            <tbody>
              {parsed.rows.slice(0, 20).map((r, i) => (
                <tr key={i} className="border-t border-border">
                  {parsed.headers.map((h) => <td key={h} className="px-2 py-1">{r.raw[h]}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {result ? (
        <div className="bg-white border border-border rounded-xl p-4 space-y-3" data-testid="bulk-result">
          <div className="flex items-center gap-4">
            <div className="text-sm font-semibold text-gray-900">Result:</div>
            <span className="inline-flex items-center gap-1 text-sm text-green-700" data-testid="bulk-result-ok">
              <CheckCircle2 className="w-4 h-4" /> {result.submission.successRows} OK
            </span>
            <span className="inline-flex items-center gap-1 text-sm text-red-700" data-testid="bulk-result-err">
              <XCircle className="w-4 h-4" /> {result.submission.errorRows} ERROR
            </span>
            <span className="text-xs text-muted ml-auto">submission {result.submission.id.slice(0, 8)}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-2 py-1">Row</th>
                  <th className="text-left px-2 py-1">Status</th>
                  <th className="text-left px-2 py-1">Mail item</th>
                  <th className="text-left px-2 py-1">Error</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`bulk-row-${r.rowIndex}`}>
                    <td className="px-2 py-1 font-mono">{r.rowIndex + 1}</td>
                    <td className={`px-2 py-1 font-semibold ${r.status === 'OK' ? 'text-green-700' : 'text-red-700'}`}>
                      {r.status}
                    </td>
                    <td className="px-2 py-1 font-mono text-[10px]">{r.mailItemId ? r.mailItemId.slice(0, 8) : '—'}</td>
                    <td className="px-2 py-1 text-red-700">{r.errorMessage ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="bg-white border border-border rounded-xl p-4">
        <h3 className="font-semibold text-gray-900 mb-2">Recent submissions</h3>
        {history.length === 0 ? (
          <p className="text-sm text-muted">No submissions yet.</p>
        ) : (
          <ul className="space-y-1 text-xs" data-testid="bulk-history">
            {history.map((h) => (
              <li key={h.id} className="flex items-center justify-between border-b border-border py-1">
                <span className="font-mono">{h.filename}</span>
                <span className="text-muted">
                  {h.successRows}/{h.totalRows} ok · {h.errorRows} err · {new Date(h.createdAt).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
